import _ from 'lodash';
import moment from 'moment';
import { FrameworkService } from '@/services/framework.service';
import { StateService } from '@/services/state.service';

export const FilterTypes = {
	interest: 'interest',
	interestFormat: 'interestFormat',
	className: 'departmentDescription',
	timeOfDay: 'timeOfDay',
	duration: 'duration',
	age: 'age',
	skillLevel: 'skillLevel',
	intensity: 'intensity',
	instructor: 'instructor',
	trainer: 'trainer',
	trainerProfilePath: 'trainerProfilePath',
	location: 'location',
	freeOnly: 'freeOnly',
	freeOnlyEvents: 'freeOnlyEvents',
	resource: 'resource',
	member: 'member',
	reservationType: 'reservationType',
	startDate: 'startDate',
	endDate: 'endDate',
	date: 'date',
	service: 'service'
};

const filteringControls = {
	multiselect: {
		name: 'multiselect',
		build(config, data, idPrefix, savedSearchFilters, queryFilters, firstRun) {
			const facetData = _.find(data, (f) => {
				return f.filter === config.facet;
			});

			let mappedData = config.mapping(facetData, config.id, savedSearchFilters, config.tagPrefix);
			if (config.grouping) mappedData = config.grouping(mappedData);
			if (config.sorting) mappedData = config.sorting(mappedData);

			return config.build(config, mappedData, idPrefix, queryFilters || [], firstRun);
		},
		findQueryFilters(config) {
			const _selectedFilters = [];
			let _selected = StateService.getQuery(config.id);
			if (!_selected) return _selectedFilters;
			if (typeof _selected !== 'object') _selected = [_selected];

			if (_selected.length > 0) {
				_.forEach(_selected, item => {
					const itemArr = item.split('|');
					let itemName = itemArr[itemArr.length - 1];
					if (typeof config.getChicletName === 'function') itemName = config.getChicletName(itemArr);

					const itemAdd = {
						name: itemName,
						value: config.tagPrefix ? config.tagPrefix + item : item,
						type: config.id,
						query: true
					};
					_selectedFilters.push(Object.assign({}, itemAdd));
				});
			}
			return _selectedFilters;
		},
		findSavedFilters(savedFilters, config) {
			const _items = [];
			for (let item of savedFilters) {
				const itemArr = item.split('|');
				let itemName = itemArr[itemArr.length - 1];
				if (typeof config.getChicletName === 'function') itemName = config.getChicletName(itemArr);
				_items.push({
					name: itemName,
					value: config.tagPrefix + item,
					type: config.id,
					saved: true
				});
			}
			return _items;
		}
	},
	toggle: {
		name: 'toggle',
		build(config, data, idPrefix, savedSearchFilters, queryFilters) {
			let _checked = false;
			if (queryFilters.length > 0) _checked = queryFilters[0];
			return config.build(config, _checked, idPrefix);
		},
		findQueryFilters(config) {
			const _selectedFilters = [];
			let _selected = StateService.getQuery(config.id);
			if (!_selected) return _selectedFilters;
			_selectedFilters.push(Object.assign({}, {
				name: config.chiclet,
				value: true,
				type: config.id,
				query: true
			}));
			return _selectedFilters;
		},
		findSavedFilters(savedFilters, config) {
			return [{
				name: config.chiclet,
				value: true,
				type: config.id,
				saved: true
			}];
		}
	},
	dateSelect: {
		name: 'dateSelect',
		build(config, data, idPrefix) {
			let _value = StateService.getQuery(config.id);

			return config.build(config, _value, idPrefix);
		},
		findQueryFilters() {
			return [];
		},
		findSavedFilters() {
			return [];
		}
	},
	singleSelect : {
		name: 'singleSelect',
		build(config, data, idPrefix, savedSearchFilters, queryFilters, firstRun) {
			const facetData = _.find(data, (f) => {
				return f.filter === config.facet;
			});
			let mappedData = config.mapping(facetData, config.id, savedSearchFilters, config.tagPrefix);
			if (config.sorting) mappedData = config.sorting(mappedData);

			return config.build(config, mappedData, idPrefix, queryFilters || [], firstRun);
		},
		findQueryFilters() {
			return [];
		},
		findSavedFilters() {
			return [];
		}
	}
};

// config.
let filteringConfig = [
	{
		id: FilterTypes.interest,
		control: filteringControls.multiselect,
		title: 'Interest',
		tagPrefix: 'interest:',
		facet: 'tags:interest',
		saveGroup: 'int',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.interestFormat,
		control: filteringControls.multiselect,
		title: 'Interest',
		tagPrefix: 'interestFormat:',
		facet: 'tags:interestFormat',
		saveGroup: 'intFor',
		mapping: mapTermsWithTagPrefixes,
		getChicletName: (nameArr) => {
			return `${nameArr[0]} ${nameArr[1]}`;
		},
		grouping: groupByPipe,
		groupingKey: 1,
		groupingValue: 0,
		build: buildGroupFilter,
		result: addToTags
	},
	{
		id: FilterTypes.className,
		control: filteringControls.multiselect,
		title: 'Class Name',
		tagPrefix: 'departmentDescription:',
		facet: 'tags:departmentDescription',
		saveGroup: 'dep',
		mapping: mapTermsWithTagPrefixes,
		grouping: groupByPipe,
		groupingKey: 0,
		groupingValue: 1,
		build: buildGroupFilter,
		result: addToTags
	},
	{
		id: FilterTypes.timeOfDay,
		control: filteringControls.multiselect,
		title: 'Time of Day',
		tagPrefix: 'timeOfDay:',
		facet: 'tags:timeOfDay',
		saveGroup: 'tod',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortTimeOfDay,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.duration,
		control: filteringControls.multiselect,
		title: 'Duration',
		tagPrefix: '',
		facet: 'duration',
		saveGroup: 'dur',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortDurations,
		build: buildFilter,
		result: addToDurations
	},
	{
		id: FilterTypes.age,
		control: filteringControls.multiselect,
		title: 'Age',
		tagPrefix: 'age:',
		facet: 'tags:age',
		saveGroup: 'age',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAges,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.skillLevel,
		control: filteringControls.multiselect,
		title: 'Skill Level',
		tagPrefix: 'skillLevel:',
		facet: 'tags:skillLevel',
		saveGroup: 'skl',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortSkillLevels,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.intensity,
		control: filteringControls.multiselect,
		title: 'Intensity',
		tagPrefix: 'intensity:',
		facet: 'tags:intensity',
		saveGroup: 'iny',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortIntensity,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.instructor,
		control: filteringControls.multiselect,
		title: 'Instructor',
		tagPrefix: '',
		facet: 'leader.name.displayname',
		saveGroup: 'ins',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToOrganizers
	},
	{
		id: FilterTypes.trainer,
		control: filteringControls.multiselect,
		title: 'Trainer',
		tagPrefix: '',
		facet: 'leader.name.displayname',
		saveGroup: 'ins',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToOrganizers
	},
	{
		id: FilterTypes.trainerProfilePath,
		control: filteringControls.multiselect,
		title: 'Trainer',
		tagPrefix: '',
		facet: 'leader.name.displayname',
		saveGroup: 'ins',
		mapping: mapServiceFacetValues,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToOrganizers
	},
	{
		id: FilterTypes.location,
		control: filteringControls.multiselect,
		title: 'Add Club Location',
		tagPrefix: '',
		facet: 'location.name',
		saveGroup: 'loc',
		required: 'Select a club location to continue search',
		getDefault: () => {
			return StateService.getLocationName();
		},
		mapping: mapClubsToState,
		grouping: groupByPipe,
		groupingKey: 0,
		groupingValue: 1,
		build: buildGroupFilter,
		result: addToLocations
	},
	{
		id: FilterTypes.freeOnly,
		control: filteringControls.toggle,
		title: 'Free Classes',
		chiclet: 'Free',
		saveGroup: 'fre',
		build: buildToggle,
		result: setFreeOnly
	},
	{
		id: FilterTypes.freeOnlyEvents,
		control: filteringControls.toggle,
		title: 'Free Events',
		chiclet: 'Free',
		saveGroup: 'fre',
		build: buildToggle,
		result: setFreeOnly
	},
	{
		id: FilterTypes.resource,
		control: filteringControls.multiselect,
		title: 'Resource',
		tagPrefix: 'resource:',
		facet: 'tags:resource',
		saveGroup: 'res',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.member,
		control: filteringControls.multiselect,
		title: 'Member',
		tagPrefix: 'member:',
		facet: 'member',
		saveGroup: 'mem',
		setQueryFilters: (queryFilters) => {
			let memberData = FrameworkService.data('profile.members');

			if (memberData) {
				for (let i= 0; i < memberData.length; i++) {
					for (let j = 0; j < queryFilters.length; j++) {
						if (queryFilters[j].indexOf(':') > -1) {
							if (queryFilters[j].split(':')[1] === memberData[i].firstName) {
								queryFilters[j] = 'member:' + memberData[i].memberId;
							} else if (memberData[i].memberType === queryFilters[j].split(' ')[1] && memberData[i].firstName === queryFilters[j].split(' ')[0].split(':')[1]) {
								queryFilters[j] = 'member:' + memberData[i].memberId;
							}
						}
					}
				}
			}
		},
		mapping: mapMembers,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToMembers
	},
	{
		id: FilterTypes.reservationType,
		control: filteringControls.multiselect,
		title: 'Reservation Type',
		tagPrefix: 'department:',
		facet: 'reservationType',
		saveGroup: 'restyp',
		mapping: mapTermsWithTagPrefixes,
		sorting: sortAlpha,
		build: buildFilter,
		result: addToTags
	},
	{
		id: FilterTypes.serviceMulti,
		control: filteringControls.multiselect,
		title: 'Service',
		tagPrefix: '',
		facet: 'service',
		mapping: mapServiceFacetValues,
		sorting: sortAlpha,
		build: buildFilter,
		result: setSelectedService
	},

	// Start and End Datepicker
	{
		id: FilterTypes.startDate,
		control: filteringControls.dateSelect,
		title: 'Start Date',
		tagPrefix: '',
		build: buildDateSelect,
		result: setStartDate
	},
	{
		id: FilterTypes.endDate,
		control: filteringControls.dateSelect,
		title: 'End Date',
		tagPrefix: '',
		build: buildDateSelect,
		result: setEndDate
	},

	// Date (Single) Select
	{
		id: FilterTypes.date,
		control: filteringControls.singleSelect,
		title: 'Date',
		tagPrefix: '',
		facet: 'startdate',
		mapping: mapDateFacetValues,
		sorting: sortValue,
		build: buildSingleSelect,
		result: setSelectedDate
	},

	// Service (Single) Select
	{
		id: FilterTypes.service,
		control: filteringControls.singleSelect,
		title: 'Service',
		tagPrefix: '',
		facet: 'service',
		mapping: mapServiceFacetValues,
		sorting: sortAlpha,
		build: buildSingleSelect,
		result: setSelectedService
	}
];

export const FilteringServiceConfig = {
	getConfig(id) {
		return _.find(filteringConfig, (item) => {
			return item.id === id;
		});
	},
	getConfigByPrefix(id, prefix) {
		return _.find(filteringConfig, (item) => {
			return prefix + item.id === id;
		});
	}
};

// MAPPINGS

function mapTermsWithTagPrefixes(facetData, filterType, savedSearchFilters, tagPrefix) {
	if (!facetData) return [];
	const result = _.map(facetData.values, function(item) {
		return {
			name: item.value.replace(tagPrefix,''),
			value: item.value
		};
	});

	const extraFilters = _.filter(savedSearchFilters, item => {
		return item.type === filterType;
	});

	_.forEach(extraFilters, item => {
		if (_.findIndex(result, ['value', item.value]) === -1) {
			result.push({
				name: item.value.replace(tagPrefix,''),
				value: item.value
			});
		}
	});

	return result;
}

function mapDateFacetValues(facetData) {
	if (!facetData) return [];
	const result = _.map(facetData.values, function(item) {
		return {
			name: moment(item.value, 'YYYY-MM-DD').format('dddd, MMM D'),
			value: item.value
		};
	});
	if (result.length) {
		result.push({ name: 'Any Date', value: null });
	}
	return result;
}

function mapServiceFacetValues(facetData) {
	if (!facetData) return [];
	const result = _.map(facetData.values, function(item) {
		return {
			name: item.name,
			value: item.value
		};
	});
	return result;
}

function mapMembers(facetData) {
	if (!facetData) return [];
	const result = _.map(facetData.values, function(item) {
		return {
			name: item.name,
			value: item.value
		};
	});

	return result;
}

function mapClubsToState(facetData) {
	if (!facetData) return [];
	var data = _.map(FrameworkService.allClubs(), function(club) {
		// Specific mapping for Prospect experience
		if (FrameworkService.isProspectExperience()) {
			const clubState = club.country === 'Canada' ? 'Canada' : club.state;
			const clubDisplay = club.status === 'open' || !club.comingSoon ? club.name : `${club.name} (${club.comingSoon})`;

			// exclude Work Only, Living Only, Sport Only, Swim Only
			if (club.membershipLevel.indexOf('Only') > -1) return null;

			// club.status values:  open, closed, waitlist, presale
			if (club.status === 'closed') return null;

			return {
				name: clubState + '|' + clubDisplay,
				value: club.name,
				disabled: false
			};
		}

		// Specific mapping for Member experience
		const facet = _.find(facetData.values, (item) => {
			return item.value === club.name;
		});

		const clubState = club.country === 'Canada' ? 'Canada' : club.state;
		const clubDisplay = !club.comingSoon ? club.name : `${club.name} (${club.comingSoon})`;

		// club.status values:  open, closed, waitlist, presale
		if (club.status === 'open' && !facet) return null;

		return {
			name: clubState + '|' + clubDisplay,
			value: club.name,
			disabled: club.status !== 'open' || !facet
		};
	});
	return _.pickBy(data, _.identity);
}

function groupByPipe(mappedData) {
	const homeState = FrameworkService.data('club.stateName') || '';
	const _getChicletName = this.getChicletName;

	return _.chain(mappedData)
		.groupBy(g => {
			return g.name.split('|')[this.groupingKey];
		})
		.map((value, key) => {
			return {
				group: key,
				values: _.chain(value).map(item => {
					let nameChiclet = item.name.split('|')[this.groupingValue];
					if (typeof _getChicletName === 'function') nameChiclet = _getChicletName(item.name.split('|'));
					return {
						name: item.name.split('|')[this.groupingValue],
						nameChiclet: nameChiclet,
						value: item.value,
						disabled: item.disabled
					};
				})
					.orderBy('name')
					.value()
			};
		})
		.sortBy(item => {
			if (item.group === 'Group Fitness' || item.group.toLowerCase() === homeState.toLowerCase()) return '';
			if (item.group === 'Canada') return 'z';
			return item.group;
		})
		.value();
}

// SORTING

function sortAlpha(mappedData) {
	return _.sortBy(mappedData, 'name');
}

function sortValue(mappedData) {
	return _.orderBy(mappedData, ({ value }) => value || '0000', ['asc']); // Sort null value to the top
}

function sortTimeOfDay(mappedData) {
	return _.sortBy(mappedData, item => {
		switch (item.name) {
			case 'Morning':
				return 0;
			case 'Midday':
				return 1;
			case 'Evening':
				return 2;
			default:
				return 3;
		}
	});
}

function sortDurations(mappedData) {
	return _.sortBy(mappedData, item => {
		switch (item.name.toLowerCase()) {
			case 'full day':
				return 0;
			case 'half day':
				return 1;
			default:
				return 2;
		}
	});
}

function sortAges(mappedData) {
	return _.sortBy(mappedData, item => {
		switch (item.name.toLowerCase()) {
			case 'kids (3 months - 3 years)':
				return 0;
			case 'kids (3 - 6 years)':
				return 1;
			case 'kids (6 - 12 years)':
				return 2;
			case 'youth (12 - 18 years)':
				return 3;
			case 'adults (18+)':
				return 4;
			case 'seniors (55+)':
				return 5;
			default:
				return 6;
		}
	});
}

function sortSkillLevels(mappedData) {
	return _.sortBy(mappedData, item => {
		switch (item.name) {
			case 'Beginner':
				return 0;
			case 'Intermediate':
				return 1;
			case 'Advanced':
				return 2;
			default:
				return 3;
		}
	});
}

function sortIntensity(mappedData) {
	return _.sortBy(mappedData, item => {
		switch (item.name) {
			case 'Low':
				return 0;
			case 'Medium':
				return 1;
			case 'High':
				return 2;
			default:
				return 3;
		}
	});
}

// BUILDING

function buildToggle(config, checked, idPrefix) {
	return {
		control: 'toggle',
		filterType: config.id,
		export: config.saveGroup,
		id: `${idPrefix}${config.id}`,
		checked: checked,
		placeholder: config.title,
		required: false
	};
}

function buildFilter(config, mappedData, idPrefix, queryFilters, firstRun) {
	return {
		control: 'multiselect',
		filterType: config.id,
		export: config.saveGroup,
		groups: [{
			name: null,
			choices: buildChoices(mappedData, queryFilters, config, firstRun)
		}],
		id: `${idPrefix}${config.id}`,
		placeholder: config.title,
		required: config.required
	};
}

function buildGroupFilter(config, mappedData, idPrefix, queryFilters, firstRun) {
	const groups = [];
	for (let item of mappedData) {
		groups.push({
			name: item.group,
			choices: buildChoices(item.values, queryFilters, config, firstRun)
		});
	}

	return {
		control: 'multiselect',
		filterType: config.id,
		export: config.saveGroup,
		groups: groups,
		id: `${idPrefix}${config.id}`,
		placeholder: config.title,
		required: config.required
	};
}

function buildChoices(mappedData, queryFilters, config) {
	const choices = [];
	const defaultValue = typeof config.getDefault === 'function' ? config.getDefault(mappedData) : null;
	if (typeof config.setQueryFilters === 'function') config.setQueryFilters(queryFilters);
	for (let item of mappedData) {
		let isDefaultItem = item.value === defaultValue;
		choices.push({
			name: item.name,
			nameChiclet: item.nameChiclet || item.name,
			value: item.value,
			html: null,
			selected: queryFilters.includes(item.value) || isDefaultItem,
			visible: true,
			enabled: !item.disabled && (!isDefaultItem || FrameworkService.isProspectExperience()),
			showInUrl: false
		});
	}
	return choices;
}

function buildDateSelect(config, dateValue, idPrefix) {
	return {
		control: 'dateSelect',
		filterType: config.id,
		id: `${idPrefix}${config.id}`,
		value: dateValue,
		placeholder: config.title,
		required: false
	};
}

function buildSingleSelect(config, mappedData, idPrefix, queryFilters, firstRun) {
	return {
		control: 'singleSelect',
		filterType: config.id,
		choices: buildChoices(mappedData, queryFilters, config, firstRun),
		id: `${idPrefix}${config.id}`,
		placeholder: config.title,
		required: config.required
	};
}

// VALUE HANDLING

function addToTags(valuesCollection, valuesToAdd) {
	valuesCollection.tags = _.concat(valuesCollection.tags, valuesToAdd);
}
function addToDurations(valuesCollection, valuesToAdd) {
	valuesCollection.duration = _.concat(valuesCollection.duration, valuesToAdd);
}
function addToOrganizers(valuesCollection, valuesToAdd) {
	valuesCollection.organizers = _.concat(valuesCollection.organizers, valuesToAdd);
}
function addToLocations(valuesCollection, valuesToAdd) {
	valuesCollection.locations = _.concat(valuesCollection.locations, valuesToAdd);
}
function setFreeOnly(valuesCollection, valuesToSet) {
	valuesCollection.freeOnly = valuesToSet;
}
function addToMembers(valuesCollection, valuesToAdd) {
	valuesCollection.members = _.concat(valuesCollection.members, valuesToAdd);
}
function setStartDate(valuesCollection, valuesToSet) {
	valuesCollection.start = valuesToSet;
}
function setEndDate(valuesCollection, valuesToSet) {
	valuesCollection.end = valuesToSet;
}
function setSelectedDate(valuesCollection, valuesToSet) {
	valuesCollection.date = valuesToSet;
}
function setSelectedService(valuesCollection, valuesToSet) {
	valuesCollection.service = valuesToSet;
}
